import { highProfileVulnerabilities } from "@/api/useIssue.demo"
import { reorderByScore } from "../MitigationDemoCards/enrich"
import {
  ISSUE_INTERNET_FACING_IDS,
  ISSUE_KODEM_SCORE_OVERRIDES,
  PROJECT_INTERNET_FACING_IDS,
  PROJECT_KODEM_SCORE_OVERRIDES
} from "./demoOverrides.const"

import type {
  IssuesTriageResponse,
  ProjectsTriageResponse
} from "@/api/useTriage.types"
import type { InternetFacing, Issue, Project } from "@/domain/issue"
import type { TriageSort } from "../MitigationDemoCards/enrich"

// Demo-only display override (isDemo), applied to the raw API response before
// the triage selectors so the list, drawers and project rows stay consistent.
// Real values are untouched; demo data lives in demoOverrides.const.

// One helper for issues + projects; RI keeps each entity's own riskInsights type.
const applyOverride = <
  RI extends { internetFacing: InternetFacing },
  T extends {
    id: string
    kodemScore: number
    riskInsights: RI
    riskId?: string
    highProfile?: unknown
  }
>(
  entity: T,
  scores: Record<string, number>,
  internetFacingIds: Set<string>
): T => {
  let next = entity

  const { riskId } = next
  const highProfile =
    riskId &&
    highProfileVulnerabilities.find(
      ({ riskIds }) => riskIds?.[0]?.id === riskId
    )

  if (highProfile && !next.highProfile) next = { ...next, highProfile }

  const score = scores[entity.id]

  if (score !== undefined) next = { ...next, kodemScore: score }

  if (internetFacingIds.has(entity.id)) {
    next = {
      ...next,
      riskInsights: { ...next.riskInsights, internetFacing: "YES" }
    }
  }

  return next
}

const hideFixVersionBesideRemediation = (issue: Issue): Issue =>
  issue.fixInsights?.hasKodemRemediation
    ? { ...issue, fixInsights: { ...issue.fixInsights, hasFixVersion: false } }
    : issue

const overrideIssue = (issue: Issue): Issue =>
  hideFixVersionBesideRemediation(
    applyOverride(issue, ISSUE_KODEM_SCORE_OVERRIDES, ISSUE_INTERNET_FACING_IDS)
  )

const overrideProject = (project: Project): Project =>
  applyOverride(
    project,
    PROJECT_KODEM_SCORE_OVERRIDES,
    PROJECT_INTERNET_FACING_IDS
  )

export const overrideDemoIssueResponse = <R extends { data?: Issue | Issue[] }>(
  response: R,
  enabled: boolean
): R => {
  const data = response?.data
  if (!enabled || !data) return response
  return {
    ...response,
    data: Array.isArray(data) ? data.map(overrideIssue) : overrideIssue(data)
  } as R
}

export const overrideDemoIssuePages = (
  response: { pages: IssuesTriageResponse[] },
  enabled: boolean,
  sort: TriageSort
) => {
  if (!enabled) return response
  const pages = response.pages.map((page) => ({
    ...page,
    data: (page.data ?? []).map(overrideIssue)
  }))
  // Keep page 1 coherent with the shown scores (default sort); not a server sort.
  if (pages[0]) {
    pages[0] = {
      ...pages[0],
      data: reorderByScore(pages[0].data, sort, (issue) => issue.kodemScore)
    }
  }
  return { ...response, pages }
}

export const overrideDemoProjectResponse = (
  response: ProjectsTriageResponse,
  enabled: boolean,
  sort: TriageSort
): ProjectsTriageResponse => {
  const data = response?.data
  if (!enabled || !data) return response
  return {
    ...response,
    data: reorderByScore(
      data.map(overrideProject),
      sort,
      (project) => project.kodemScore
    )
  }
}
